import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  LayoutDashboard, Users, Briefcase, Megaphone, Building2,
  Truck, UserCircle, UserPlus, ClipboardList, Receipt,
  FileText, IndianRupee, BarChart3, Settings, Menu, X, LogOut,
  Activity, AlertTriangle, UserCheck, Warehouse,
  ShoppingCart, ListChecks, FileBox, GitCompare,
  Target, CalendarClock, Key, ShieldAlert, HelpCircle, BookOpen, TrendingUp,
  GitBranch, Inbox, Shield, BrainCircuit, Landmark, ArrowLeft, Wallet,
  MessageSquare, Lock,
} from 'lucide-react';
import { usePermissions } from '../hooks/usePermissions';
import { usePersona } from '../hooks/usePersona';
import { focusedDashboardLabel } from '@/lib/dashboardAccess';
import { GROUP_COLORS, isItemVisible } from '@/config/navigation';
import { recordRecentRoute } from '@/lib/recentRoutes';
import { ordersAPI, chatAPI, crmAPI, projectNOCAPI } from '@/lib/api';
import NotificationBell from './NotificationBell';
import GlobalSearch from './GlobalSearch';
import MeetingReminderWatcher from '@/components/meetings/MeetingReminderWatcher';
import JourneyBreadcrumb from '@/components/ui/JourneyBreadcrumb';

// ─── Sidebar config ────────────────────────────────────────────────────────
// Grouped instead of flat — easier to scan, mirrors the mental model the team
// uses (Operations / People / Inventory / Finance / Reports / Settings).
//
// The Overview group used to list every per-role dashboard side-by-side, which
// confused users (admin saw 6 dashboard entries, half of which they never
// open). Now each role sees a single "Dashboard" entry pointing at the home
// route they actually use; the bigger "Integrated" view is admin-only.
// Each group gets a distinct hue so the collapsed sidebar reads as a quick
// colour ladder. Icons keep their colour even when collapsed (label hidden)
// so the user can find a section by colour alone, with a hover tooltip
// confirming the destination.
//
// GROUP_COLORS, CEO_ROLES and isItemVisible now live in @/config/navigation so
// the Welcome launchpad can reuse the exact same visibility rules.

const sidebarGroups = [
  {
    label: 'Overview',
    items: [
      { path: '/', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'dashboard', excludeRoles: ['cfo'] },
      { path: '/account-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'account-dashboard' },
      { path: '/vendor-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'vendor-dashboard' },
      { path: '/client-servicing-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'client-servicing-dashboard' },
      { path: '/driver-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'driver-dashboard' },
      { path: '/operations-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'operations-dashboard' },
      { path: '/purchase-dashboard', icon: LayoutDashboard, label: 'Dashboard', menuKey: 'purchase-dashboard' },
      { path: '/cxo-dashboard', icon: TrendingUp, label: 'CEO Dashboard', menuKey: 'cxo-dashboard' },
      { path: '/cfo-dashboard', icon: TrendingUp, label: 'CFO Dashboard', menuKey: 'cfo-dashboard' },
      { path: '/md-dashboard', icon: TrendingUp, label: 'MD Command Centre', menuKey: 'md-dashboard' },
    ],
  },
  {
    label: 'Operations',
    items: [
      { path: '/clients', icon: Users, label: 'Accounts Receivable', menuKey: 'clients' },
      { path: '/projects', icon: Briefcase, label: 'Projects', menuKey: 'projects' },
      // Project Closure board. `apiGated` asks the queue endpoint whether this
      // user may read it (see closureAccess below), so the entry is never
      // offered to someone the API would answer with 403 — and never withheld
      // from someone it would allow. External portal accounts stay excluded
      // outright. Approve/Reject rights are decided per department by the
      // backend; the badge shows how many are pending this user's review.
      {
        path: '/project-closure', icon: Lock, label: 'Project Closure',
        excludeRoles: ['vendor', 'driver', 'client', 'promoter', 'anchor'],
        badge: 'projectClosure', apiGated: 'projectClosure',
      },
      { path: '/campaigns', icon: Megaphone, label: 'Campaigns', menuKey: 'campaigns' },
      { path: '/relationship-map', icon: GitBranch, label: 'Relationship Map', menuKey: 'relationship-map' },
      { path: '/operations', icon: ClipboardList, label: 'Operations', menuKey: 'operations' },
      { path: '/daily-reporting', icon: Activity, label: 'Daily Expenses', menuKey: 'promoter-activities' },
    ],
  },
  {
    label: 'People',
    items: [
      { path: '/vendors', icon: Building2, label: 'Vendors', menuKey: 'vendors' },
      { path: '/drivers', icon: Truck, label: 'Drivers', menuKey: 'drivers' },
      { path: '/driver-approval', icon: UserCheck, label: 'Driver Approval', menuKey: 'driver-approval' },
    ],
  },
  {
    label: 'Inventory',
    items: [
      { path: '/campaign-inventory', icon: Truck, label: 'Campaign Inventory', menuKey: 'campaign-inventory' },
      { path: '/warehouse-inventory', icon: Warehouse, label: 'Warehouses', menuKey: 'warehouse-inventory' },
      { path: '/godown-inventory', icon: Warehouse, label: 'Godowns', menuKey: 'godown-inventory' },
    ],
  },
  {
    label: 'Purchase',
    items: [
      { path: '/purchase/requests', icon: ShoppingCart, label: 'Purchase Requests', menuKey: 'purchase-requests' },
      { path: '/purchase/rfqs', icon: GitCompare, label: 'RFQs', menuKey: 'purchase-rfqs' },
      { path: '/purchase/orders', icon: FileBox, label: 'Purchase Orders', menuKey: 'purchase-orders' },
      // OFM operations page — Finance Control (Chitra) only. The one place to
      // review OFM requests and Create PO / Reject.
      { path: '/ofm', icon: Inbox, label: 'OFM Requests', financeControlOnly: true },
    ],
  },
  {
    label: 'Finance',
    items: [
      { path: '/accounts', icon: IndianRupee, label: 'Accounts & Payments', menuKey: 'finance' },
      { path: '/accounts/payment-requests', icon: Receipt, label: 'Payment Requests', menuKeys: ['finance', 'payment-requests'] },
      { path: '/purchase/po-payments', icon: Wallet, label: 'PO Payments', menuKey: 'finance' },
      { path: '/accounts/advances', icon: IndianRupee, label: 'Advance Reconciliation', menuKey: 'finance', excludeRoles: ['cfo'] },
      { path: '/accounts/payment-allocations', icon: Wallet, label: 'Bill-wise Adjustment', menuKey: 'finance' },
      { path: '/accounts/overheads', icon: Landmark, label: 'Overheads (Tally)', menuKey: 'finance' },
      { path: '/accounts/payments-tracker', icon: Wallet, label: 'Payments Tracker', menuKey: 'finance' },
      { path: '/accounts/billing-corrections', icon: GitCompare, label: 'Billing Corrections', menuKey: 'finance', badge: 'billingCorrections' },
      { path: '/invoices', icon: FileText, label: 'Invoices (Tally)', menuKey: 'finance' },
      { path: '/reports/fl-pnl', icon: IndianRupee, label: 'FL Cost-Centre P&L', menuKey: 'finance' },
      { path: '/reports/retainers', icon: CalendarClock, label: 'Retainers', menuKey: 'finance' },
    ],
  },
  {
    label: 'Expenses',
    items: [
      { path: '/expenses', icon: Receipt, label: 'Expenses', menuKey: 'expenses', excludeRoles: ['cfo'] },
    ],
  },
  {
    label: 'Reports',
    items: [
      { path: '/reports', icon: FileText, label: 'Reports', menuKey: 'reports', excludeRoles: ['cfo'] },
      { path: '/report-templates', icon: ClipboardList, label: 'Reporting Formats', menuKeys: ['reports', 'campaigns'], excludeRoles: ['cfo'] },
      { path: '/campaign-reports', icon: BarChart3, label: 'Campaign Reports', menuKey: 'reports', excludeRoles: ['cfo'] },
    ],
  },
  {
    label: 'Analytics',
    items: [
      { path: '/business-intelligence', icon: BrainCircuit, label: 'Business Intelligence', menuKey: 'business-intelligence' },
      { path: '/analytics', icon: BarChart3, label: 'Analytics', menuKey: 'analytics', excludeRoles: ['cfo'] },
    ],
  },
  {
    label: 'Tasks',
    items: [
      { path: '/tasks', icon: ListChecks, label: 'Tasks', menuKey: 'tasks' },
    ],
  },
  {
    label: 'CRM',
    items: [
      { path: '/crm', icon: Target, label: 'Opportunities', menuKey: 'crm' },
      { path: '/crm/targets', icon: Target, label: 'Target Accounts', menuKey: 'crm' },
      { path: '/orders', icon: FileText, label: 'Orders (FL)', menuKey: 'crm' },
      { path: '/client-pos', icon: FileText, label: 'Client POs', menuKey: 'crm' },
      { path: '/crm/invoices', icon: Receipt, label: 'Invoices', menuKey: 'crm' },
      // Approval inbox — surfaces pending order amendments awaiting THIS user's
      // approval (scoped via org hierarchy on the backend). Without this nav
      // entry pending amendments sit forever.
      { path: '/orders/approvals', icon: Inbox, label: 'Approvals', menuKey: 'crm', badge: 'orderApprovals' },
      // Estimate-template approvals — user-created templates await THIS user's
      // sign-off (their reporting-manager queue). Own nav entry + badge so a
      // reviewer sees them without opening the estimate editor.
      { path: '/crm/estimate-templates/approvals', icon: Inbox, label: 'Template Approvals', menuKey: 'crm', badge: 'estimateTemplateApprovals' },
      { path: '/crm/activities', icon: Activity, label: 'Activities', menuKey: 'crm' },
      { path: '/crm/accounts', icon: Users, label: 'Accounts', menuKey: 'crm' },
      { path: '/crm/archived', icon: FileBox, label: 'Archived', menuKey: 'crm' },
      { path: '/crm/automation', icon: GitCompare, label: 'Automation', menuKey: 'crm' },
      { path: '/analytics/profitability', icon: IndianRupee, label: 'Campaign P&L', menuKey: 'finance', excludeRoles: ['cfo'] },
      { path: '/analytics/crm-pipeline', icon: Target, label: 'Pipeline Analytics', menuKey: 'crm' },
    ],
  },
  {
    // Van Expense — parallel additive section (Phase 1, UI-only). A new entry
    // point over existing functionality; gated by the existing 'projects' menu
    // key so it inherits current permissions without adding any. Existing
    // groups/items above are unchanged.
    label: 'Van Expense',
    items: [
      { path: '/van-expense', icon: Truck, label: 'Van Expense', menuKey: 'projects', excludeRoles: ['cfo'] },
    ],
  },
  {
    label: 'Settings',
    items: [
      { path: '/settings/user-registration', icon: UserPlus, label: 'New Registration', menuKey: 'user-registration' },
      { path: '/settings/employees', icon: Users, label: 'Employees', menuKey: 'employees' },
      { path: '/settings/employee-import', icon: UserPlus, label: 'Import HR XLSX', menuKey: 'employee-import' },
      { path: '/settings', icon: Settings, label: 'Settings', menuKey: 'settings' },
      // Audit log — visibility gated on `superadminOnly`, checked by the
      // hasItemAccess filter below. Page-level guard is also enforced.
      { path: '/admin/audit-log', icon: Shield, label: 'Audit Log', menuKey: 'audit-log', superadminOnly: true },
      { path: '/admin/tally-mappings', icon: GitCompare, label: 'Tally FL Remapping', menuKey: 'tally-mappings', superadminOnly: true },
    ],
  },
  {
    // Collaboration (Chat + Meetings) sits just above Help and is available to
    // every authenticated user (no menuKey gate), so the heading shows for all.
    label: 'Chat',
    items: [
      { path: '/chat', icon: MessageSquare, label: 'Chat', badge: 'chatUnread' },
      { path: '/meetings', icon: CalendarClock, label: 'Meetings' },
    ],
  },
  {
    label: 'Help',
    items: [
      { path: '/knowledge-base', icon: BookOpen, label: 'Knowledge Base' },
      { path: '/help', icon: HelpCircle, label: 'Help Manual' },
    ],
  },
];

const primaryDashboardItem = ({ user, team, role, isSuperadmin }) => {
  const accessGroup = team || role || user?.role;
  if (accessGroup === 'leadership' || isSuperadmin) {
    return {
      path: '/',
      icon: LayoutDashboard,
      label: focusedDashboardLabel({ user, team: accessGroup, isSuperadmin }),
      menuKey: 'dashboard',
    };
  }

  if (accessGroup === 'creative') {
    return {
      path: '/creative-dashboard',
      icon: LayoutDashboard,
      label: 'Creative Dashboard',
      menuKey: 'dashboard',
    };
  }

  return null;
};

const Layout = () => {
  // Sidebar collapsed/expanded on desktop, hidden by default on mobile.
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Drilled in from the MD Command Centre? Show a one-click way back so a deep
  // detail page never strands the user (state set by MD tiles: {fromMD:true}).
  const cameFromMD = location.state?.fromMD && location.pathname !== '/md-dashboard';
  const { isMenuVisible, loading, role, team, isSuperadmin } = usePermissions();
  const { loading: personaLoading, dashboardRoute } = usePersona();
  // Whether to offer Project Closure at all. `my-permissions` lists only the
  // permissions saved in the role_permissions TABLE, while the endpoint's
  // require_permission ALSO falls back to the hardcoded matrix in
  // role_permissions.py — so roles like accounts and purchase pass the API but
  // carry no project_noc.read string, and gating the entry on that string
  // hides the board from the very departments that own its NOCs. Asking the
  // endpoint keeps the menu and the API on one gate, whichever grants it.
  // Shares its query key (and therefore its single request) with the badge;
  // offered while the probe is in flight, withdrawn only on a real refusal.
  const { isError: closureDenied } = useQuery({
    queryKey: ['project-noc-queue-counts'],
    queryFn: () => projectNOCAPI.getQueueCounts(),
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const closureAccess = !closureDenied;
  const user = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const displayRole = team || role || user.role;
  const focusedDashboard = useMemo(
    () => primaryDashboardItem({ user, team, role, isSuperadmin }),
    [user, team, role, isSuperadmin]
  );

  const visibleGroups = useMemo(() => {
    if (loading || personaLoading) return [];
    // Replace the role-based "Dashboard" entry with the persona dashboard
    // route when one applies, so the sidebar's Dashboard link points at the
    // user's actual landing page.
    const adapted = sidebarGroups.map((group) => {
      if (group.label !== 'Overview') return group;
      if (focusedDashboard) {
        // Keep the CEO Command Centre alongside the focused dashboard so
        // leadership roles see both their primary dashboard AND the CXO one.
        // (Visibility is still decided by isItemVisible -> menu matrix below.)
        const ceoItems = group.items.filter((item) => item.menuKey === 'cxo-dashboard');
        return {
          ...group,
          items: [focusedDashboard, ...ceoItems],
        };
      }
      if (!dashboardRoute) return group;
      return {
        ...group,
        items: group.items.map((item) =>
          item.menuKey === 'dashboard'
            ? { ...item, path: dashboardRoute }
            : item
        ),
      };
    });
    return adapted
      .map((group) => ({
        ...group,
        items: group.items
          .filter((item) => isItemVisible(item, isMenuVisible, isSuperadmin, user, displayRole))
          // Pages whose backend gate is a permission rather than a menu key:
          // ask the endpoint instead of re-deriving the rule here. See
          // closureAccess below for why the permission list cannot answer this.
          .filter((item) => item.apiGated !== 'projectClosure' || closureAccess),
      }))
      .filter((group) => group.items.length > 0);
  }, [isMenuVisible, loading, personaLoading, dashboardRoute, focusedDashboard, user, isSuperadmin, displayRole, closureAccess]);

  // Auto-close mobile drawer when navigating
  useEffect(() => {
    setMobileOpen(false);
  }, [navigate]);

  // Record visited routes so the Welcome launchpad can show "Jump back in".
  // Skip the launchpad itself and auth/utility routes — they aren't worth
  // surfacing as recents.
  useEffect(() => {
    recordRecentRoute(location.pathname);
  }, [location.pathname]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    localStorage.removeItem('menu_items');
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');
    navigate('/login');
  };

  // When a superadmin uses "Login as X", we stash their original token under
  // `impersonator_token` and swap in the target user's token. The banner below
  // reads that key to decide whether to render itself.
  const impersonatorToken = typeof window !== 'undefined'
    ? localStorage.getItem('impersonator_token')
    : null;
  const impersonatorUser = useMemo(() => {
    if (!impersonatorToken) return null;
    try {
      return JSON.parse(localStorage.getItem('impersonator_user') || '{}');
    } catch {
      return {};
    }
  }, [impersonatorToken]);

  const handleExitImpersonation = () => {
    const originalToken = localStorage.getItem('impersonator_token');
    const originalUser = localStorage.getItem('impersonator_user');
    if (!originalToken) return;
    localStorage.setItem('token', originalToken);
    if (originalUser) localStorage.setItem('user', originalUser);
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');
    // Perms/menu come back on the next page render via /auth/me + permissions provider.
    window.location.href = '/';
  };

  const sidebarWidth = sidebarOpen ? 'w-64' : 'w-16';
  const mainOffset = sidebarOpen ? 'md:ml-64' : 'md:ml-16';

  return (
    <div className="min-h-screen bg-background">
      <MeetingReminderWatcher />
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div
          aria-hidden
          className="fixed inset-0 bg-foreground/30 z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <aside
        data-testid="sidebar"
        className={`fixed left-0 top-0 h-full bg-card border-r border-border/60 z-50 transition-transform duration-300
          ${sidebarWidth}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
      >
        <div className="flex items-center justify-between px-5 py-5 border-b border-border/60">
          {sidebarOpen && (
            <a href="/" className="flex items-center gap-2">
              {/* Compact brand mark: rounded-square wrapper around a clipboard glyph
                  in the primary indigo. Stands in for a dedicated logo until the
                  team supplies one. */}
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <ClipboardList size={18} strokeWidth={2.25} />
              </span>
              <h1 className="text-lg font-display text-foreground">
                report360<span className="text-primary">.</span>
              </h1>
            </a>
          )}
          <button
            data-testid="toggle-sidebar-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 hover:bg-secondary rounded-md transition-colors hidden md:block text-muted-foreground hover:text-foreground"
            aria-label="Toggle sidebar"
          >
            {sidebarOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        <nav className="p-3 space-y-3 overflow-y-auto h-[calc(100vh-140px)]">
          {visibleGroups.map((group) => {
            const palette = GROUP_COLORS[group.label] || GROUP_COLORS.Settings;
            return (
              <div key={group.label}>
                {sidebarOpen ? (
                  <div className="px-3 pb-1.5 text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-semibold">
                    {group.label}
                  </div>
                ) : (
                  // Thin colour-coded divider between groups when collapsed,
                  // so icons don't run together as one wall.
                  <div className={`mx-2 mb-2 mt-1 h-[2px] rounded-full ${palette.tint}`} />
                )}
                <div className={sidebarOpen ? 'space-y-0.5' : 'space-y-1.5'}>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.path === '/'}
                      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                      // Native tooltip — invaluable when sidebar is collapsed.
                      title={item.label}
                      className={({ isActive }) =>
                        // Expanded: BankDash-style 5px left-edge accent on active.
                        // Collapsed: centered icon in a coloured chip with a
                        // subtle ring; active gets a stronger tint + ring.
                        sidebarOpen
                          ? `relative flex items-center gap-3.5 px-4 py-3 transition-colors group ${
                              isActive
                                ? 'text-primary font-semibold before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[5px] before:rounded-r-full before:bg-primary'
                                : 'text-slate-500 hover:text-slate-900'
                            }`
                          : `mx-auto flex h-9 w-9 items-center justify-center rounded-lg ring-1 transition-all ${
                              isActive
                                ? `${palette.tint} ${palette.ring} shadow-sm`
                                : 'bg-white/60 ring-transparent hover:ring-slate-200 hover:bg-slate-50'
                            }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <item.icon
                            size={sidebarOpen ? 20 : 18}
                            strokeWidth={isActive ? 2.25 : 1.85}
                            className={
                              isActive
                                ? sidebarOpen ? 'text-primary' : palette.inkActive
                                : sidebarOpen
                                  ? 'text-slate-400 group-hover:text-slate-700'
                                  : palette.ink
                            }
                          />
                          {sidebarOpen && <span className="text-[15px]">{item.label}</span>}
                          {/* Optional count badge — driven by the item's `badge`
                              key. Right now only used for order-amendment
                              approvals so the reviewer can see them without
                              opening the page. */}
                          {item.badge === 'orderApprovals' && (
                            <OrderApprovalsBadge sidebarOpen={sidebarOpen} />
                          )}
                          {item.badge === 'estimateTemplateApprovals' && (
                            <EstimateTemplateApprovalsBadge sidebarOpen={sidebarOpen} />
                          )}
                          {item.badge === 'chatUnread' && (
                            <ChatUnreadBadge sidebarOpen={sidebarOpen} />
                          )}
                          {item.badge === 'billingCorrections' && (
                            <BillingCorrectionsBadge sidebarOpen={sidebarOpen} />
                          )}
                          {item.badge === 'projectClosure' && (
                            <ProjectClosureBadge sidebarOpen={sidebarOpen} />
                          )}
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full border-t border-border/60 p-3">
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-3 px-2.5 py-2 w-full rounded-md text-muted-foreground hover:bg-secondary/60 hover:text-foreground transition-colors"
          >
            <LogOut size={16} strokeWidth={1.75} />
            {sidebarOpen && <span className="text-[13px]">Sign out</span>}
          </button>
        </div>
      </aside>

      <main className={`transition-all duration-300 ${mainOffset}`}>
        {impersonatorToken && (
          <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 md:px-8 py-2 sticky top-0 z-40 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <ShieldAlert size={16} className="text-amber-700" />
              <span>
                Logged in as <strong>{user.name || user.email || 'user'}</strong>
                {impersonatorUser?.name && (
                  <> &middot; impersonating from <strong>{impersonatorUser.name}</strong></>
                )}
              </span>
            </div>
            <button
              type="button"
              onClick={handleExitImpersonation}
              className="text-xs font-semibold underline hover:text-amber-700"
            >
              Exit impersonation
            </button>
          </div>
        )}
        {/* BankDash-style header: brand-style search pill on the right, plus
            small icon buttons and an avatar circle. Page title is rendered by
            individual pages (Dashboard.js et al), not here. */}
        <header className="bg-card border-b border-border/60 px-4 md:px-8 py-4 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              className="p-2 -ml-2 rounded-md hover:bg-secondary md:hidden text-muted-foreground hover:text-foreground"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <Menu size={20} />
            </button>
            <div className="flex-1 min-w-0 flex items-center justify-end gap-3">
              <GlobalSearch />
              <NotificationBell />
              <NavLink
                to="/change-password"
                data-testid="header-change-password-link"
                className="hidden md:inline-flex h-9 w-9 items-center justify-center rounded-full bg-secondary text-muted-foreground hover:text-foreground"
                title="Change password"
                aria-label="Change password"
              >
                <Key size={16} />
              </NavLink>
              <NavLink
                to="/my-account"
                data-testid="header-account-link"
                className="hidden md:inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 hover:bg-secondary/60 transition-colors"
                aria-label="My account"
                title={user.name || 'My account'}
              >
                <span className="h-9 w-9 rounded-full bg-primary/10 text-primary inline-flex items-center justify-center text-sm font-semibold">
                  {(user.name || 'U').slice(0, 1).toUpperCase()}
                </span>
                {user.name && (
                  <span className="text-sm font-medium text-foreground hidden lg:inline">
                    {user.name}
                  </span>
                )}
              </NavLink>
              {displayRole && (
                <span className="rounded-full bg-secondary text-foreground text-[11px] font-semibold px-3 py-1 capitalize hidden lg:inline">
                  {String(displayRole).replace(/_/g, ' ')}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="p-4 md:p-6">
          {cameFromMD && (
            <button
              onClick={() => navigate('/md-dashboard')}
              className="mb-4 inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-md hover:bg-indigo-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" /> MD Command Centre
            </button>
          )}
          <JourneyBreadcrumb />
          <Outlet />
        </div>
      </main>
    </div>
  );
};

// ─── Sidebar count badge for order-amendment approvals ───────────────────────
//
// Polls /orders/amendments/pending-for-me every 60s so a fresh amendment
// raised by a teammate becomes visible without a refresh. Hidden when count = 0
// (no point drawing user attention to an empty inbox).

function ChatUnreadBadge({ sidebarOpen }) {
  const { data } = useQuery({
    queryKey: ['chat-unread'],
    queryFn: () => chatAPI.unreadCounts(),
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });
  const count = data?.total || 0;
  if (!count) return null;
  const label = count > 99 ? '99+' : count;
  return sidebarOpen ? (
    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
      {label}
    </span>
  ) : (
    <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
      {label}
    </span>
  );
}

// New (pending) billing-correction requests waiting for Accounts. Shares the
// summary query cache with the Accounts page, so it updates the moment a request
// is corrected/rejected there. Gated to finance.update (the endpoint requires it).
function BillingCorrectionsBadge({ sidebarOpen }) {
  const { hasPermission } = usePermissions();
  const { data } = useQuery({
    queryKey: ['billing-correction-requests', 'summary'],
    queryFn: () => ordersAPI.billingCorrectionRequestsSummary(),
    enabled: hasPermission('finance.update'),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const count = data?.pending || 0;
  if (!count) return null;
  return sidebarOpen ? (
    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
      {count}
    </span>
  ) : (
    <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
      {count}
    </span>
  );
}

// Project Closure: how many closures are waiting on THIS user. The endpoint
// applies the same department scope and the same permission gate as the page,
// so the number is the page's Pending — never a global tally of closures the
// user cannot open. Only Pending is shown: it is the one that asks for work,
// and the board itself carries the Approved / Rejected / All breakdown.
// Hidden at zero, like every other sidebar count here.
function ProjectClosureBadge({ sidebarOpen }) {
  // Same query key as the access probe in Layout, so react-query serves both
  // from one request and the badge can never outlive the entry it sits on.
  const { data } = useQuery({
    queryKey: ['project-noc-queue-counts'],
    queryFn: () => projectNOCAPI.getQueueCounts(),
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const count = data?.pending || 0;
  if (!count) return null;
  const label = count > 99 ? '99+' : count;
  const title = `${count} project ${count === 1 ? 'closure' : 'closures'} pending your review`;

  return sidebarOpen ? (
    <span
      title={title}
      className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white"
    >
      {label}
    </span>
  ) : (
    <span
      title={title}
      className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white"
    >
      {label}
    </span>
  );
}

function OrderApprovalsBadge({ sidebarOpen }) {
  const { data } = useQuery({
    queryKey: ['pending-order-approvals'],
    queryFn: () => ordersAPI.listPendingAmendmentsForMe(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const count = data?.total || 0;
  if (!count) return null;
  return sidebarOpen ? (
    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
      {count}
    </span>
  ) : (
    <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
      {count}
    </span>
  );
}

function EstimateTemplateApprovalsBadge({ sidebarOpen }) {
  const { data } = useQuery({
    queryKey: ['estimate-templates-pending'],
    queryFn: () => crmAPI.listPendingEstimateTemplates(),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });
  const count = Array.isArray(data) ? data.length : 0;
  if (!count) return null;
  return sidebarOpen ? (
    <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1.5 text-[11px] font-semibold text-white">
      {count}
    </span>
  ) : (
    <span className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-white">
      {count}
    </span>
  );
}


export default Layout;
